import { get } from 'lodash';
import { CHANNEL_TYPE_TIKTOK, CHANNEL_TYPE_YOUTUBE, getTiktokEmbedUrl, getYoutubeEmbedUrl, getYoutubeVideoThumbnail } from './channels';

import type { CampaignAsset, GalleryModalItem, MediaPreviewAttachment } from 'types';
import { getCloudinaryVideoPublicId, getCloudinaryVideoThumbnail } from 'utils/cloudinary';
import { isCloudinaryUrl, isS3Url, isTikTokVideoUrl, isYoutubeUrl } from 'utils/url';
import { getTikTokThumbnailUrlFromVideoUrl } from 'utils/tiktok-thumbnail';

export const MAX_VIDEO_SIZE = 41943040; // Cloudinary default max video file size
export const MAX_IMAGE_SIZE = 20971520; // Cloudinary default max image file size
export const MAX_VIDEO_SIZE_IN_MB = 40;
export const MAX_IMAGE_SIZE_IN_MB = 20;
export const CAMPAIGN_ASSET_IMAGE = 'image';
export const CAMPAIGN_ASSET_VIDEO = 'video';
export const CAMPAIGN_ASSET_PDF = 'pdf';

function checkType(asset: CampaignAsset | undefined | null, state: string): boolean {
  return asset && asset.assetType === state || false;
}

export function isVideoOnCloudinary(asset?: CampaignAsset | null): boolean {
  return checkType(asset, CAMPAIGN_ASSET_VIDEO) && isCloudinaryUrl(asset?.url) || false;
}

export function isVideoOnYoutube(asset?: CampaignAsset | null): boolean {
  return checkType(asset, CAMPAIGN_ASSET_VIDEO) && !isVideoOnCloudinary(asset) && isYoutubeUrl(asset?.url);
}

export function isVideoOnTikTok(asset?: CampaignAsset | null): boolean {
  return checkType(asset, CAMPAIGN_ASSET_VIDEO) && !isVideoOnCloudinary(asset) && isTikTokVideoUrl(asset?.url);
}

export function prepareCampaignAssetsForGallery(assets: Array<CampaignAsset>): Array<GalleryModalItem> {
  return assets.map(asset => {
    if (isVideoOnCloudinary(asset)) {
      return ({
        type: 'cloudinary-video',
        assetUrl: asset.url,
        url: asset.url,
        cloudinaryVideoId: get(asset, 'meta.cloudinary.publicId', getCloudinaryVideoPublicId(asset.url))
      });
    }

    if (isVideoOnYoutube(asset)) {
      return ({
        type: CHANNEL_TYPE_YOUTUBE,
        url: asset.url,
        embedUrl: getYoutubeEmbedUrl(asset.url),
        assetUrl: getYoutubeVideoThumbnail(asset.url)
      });
    }

    if (isVideoOnTikTok(asset)) {
      return ({
        type: CHANNEL_TYPE_TIKTOK,
        url: asset.url,
        embedUrl: getTiktokEmbedUrl(asset.url),
        assetUrl: asset.url
      });
    }

    if (asset.assetType === 'pdf') {
      return ({
        type: 'cloudinary-pdf',
        url: asset.url,
        assetUrl: asset.url,
        fileName: asset.fileName
      });
    }

    if (asset.assetType === 'video' && isS3Url(asset.url)) {
      return ({
        type: 'collabary-video',
        url: asset.url,
        assetUrl: asset.url,
        fileName: asset.fileName
      });
    }

    return ({
      type: 'cloudinary-image',
      assetUrl: asset.url,
      url: asset.url
    });
  });
}

export function prepareCampaignAssetForMediaPreview(asset: CampaignAsset): MediaPreviewAttachment {
  const isYoutube = isVideoOnYoutube(asset);

  return {
    id: asset.id,
    url: isYoutube ? getYoutubeVideoThumbnail(asset.url) : asset.url,
    format: asset.assetType,
    fileName: asset.fileName
  };
}

export function getCampaignAssetsPreviews(assets: Array<CampaignAsset>): Array<Promise<string>> {
  return assets.map((asset) => {
    if (isVideoOnCloudinary(asset)) {
      return Promise.resolve(getCloudinaryVideoThumbnail(asset.url));
    }

    if (isVideoOnYoutube(asset)) {
      return Promise.resolve(getYoutubeVideoThumbnail(asset.url));
    }

    if (isVideoOnTikTok(asset)) {
      return getTikTokThumbnailUrlFromVideoUrl(asset.url).catch(() => '');
    }

    return Promise.resolve(asset.url);
  });
}
